/**
 * chain-eos/resources.ts
 *
 * EOS resource management: PowerUp (CPU/NET), RAM buy/sell.
 * Greymass Fuel handles most cases automatically, but users may want
 * to manually PowerUp or buy RAM.
 */

import { Asset, Name, UInt64 } from '@wharfkit/antelope'
import type { Session } from './session.js'
import { EOS_RPC_ENDPOINTS, SYSTEM_CONTRACT } from './config.js'
import { fetchRpc } from './rpc-fetch.js'

export interface PowerUpParams {
  payer: string
  receiver: string
  cpuFrac: number // fraction of total CPU (0 to 1_000_000_000_000_000)
  netFrac: number // fraction of total NET
  maxPayment: string // e.g. "1.0000 EOS"
}

export interface BuyRamParams {
  payer: string
  receiver: string
  bytes: number
}

/** Matches `powerup_state_resource` from eosio.system (subset for pricing). */
interface PowerupStateResource {
  weight?: string | number
  weight_ratio?: string | number
  exponent?: number
  min_price?: unknown
  max_price?: unknown
  utilization?: string | number
  adjusted_utilization?: string | number
}

interface PowupStateRow {
  cpu?: PowerupStateResource
  net?: PowerupStateResource
}

// ─── PowerUp ─────────────────────────────────────────────────────────────────

export async function powerUp(
  session: Session,
  params: PowerUpParams,
): Promise<string> {
  const maxPaymentAsset = Asset.from(params.maxPayment)

  const result = await session.transact({
    action: {
      account: SYSTEM_CONTRACT,
      name: 'powerup',
      authorization: [{ actor: params.payer, permission: 'active' }],
      data: {
        payer: Name.from(params.payer),
        receiver: Name.from(params.receiver),
        days: 1,
        net_frac: UInt64.from(params.netFrac),
        cpu_frac: UInt64.from(params.cpuFrac),
        max_payment: maxPaymentAsset,
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

/**
 * Does this account exist on chain?
 *
 * Used before buying RAM for somebody else: `buyrambytes` to a non-existent
 * account fails on chain anyway, but checking first turns a burnt transaction
 * and a cryptic error into an inline hint about a typo.
 */
export async function accountExists(account: string): Promise<boolean> {
  const name = String(account ?? '').trim()
  if (!name || !/^[a-z1-5.]{1,13}$/.test(name)) return false
  for (const url of EOS_RPC_ENDPOINTS) {
    try {
      const res = await fetchRpc(url, '/v1/chain/get_account', { account_name: name })
      // Any HTTP response — even an error status — means this endpoint is
      // reachable and gave a definitive on-chain answer: 200 = exists,
      // anything else = "unknown_key_value_exception" (missing account).
      // Different nodes report that as 500 (stock nodeos), 400, or 404
      // depending on the proxy in front of them, so we don't special-case
      // the status code here. Only a thrown error (CORS block, DNS
      // failure, timeout — no response at all) falls through to try the
      // next endpoint.
      return res.status === 200
    } catch {
      // Endpoint unreachable — try the next one.
    }
  }
  // All endpoints unreachable — do not claim the account is missing.
  return true
}

// ─── RAM ─────────────────────────────────────────────────────────────────────

export async function buyRam(
  session: Session,
  params: BuyRamParams,
): Promise<string> {
  const result = await session.transact({
    action: {
      account: SYSTEM_CONTRACT,
      name: 'buyrambytes',
      authorization: [{ actor: params.payer, permission: 'active' }],
      data: {
        payer: Name.from(params.payer),
        receiver: Name.from(params.receiver),
        bytes: params.bytes,
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

export async function sellRam(
  session: Session,
  account: string,
  bytes: number,
): Promise<string> {
  const result = await session.transact({
    action: {
      account: SYSTEM_CONTRACT,
      name: 'sellram',
      authorization: [{ actor: account, permission: 'active' }],
      data: {
        account: Name.from(account),
        bytes: UInt64.from(bytes),
      },
    },
  })

  return (result.response as { transaction_id?: string })?.transaction_id ?? ''
}

function parseInt64(v: unknown): number {
  if (typeof v === 'number') return Number.isFinite(v) ? v : NaN
  if (typeof v === 'string') return Number.parseInt(v, 10)
  return NaN
}

/** Asset amount in smallest on-chain units (same as eosio `asset.amount`). */
function parseAssetAmount(asset: unknown): number {
  if (asset == null) return 0
  if (typeof asset === 'string') {
    const parts = asset.trim().split(/\s+/)
    if (parts.length >= 1) {
      const n = Number.parseFloat(parts[0])
      if (!Number.isFinite(n)) return 0
      return Math.round(n * 10_000)
    }
  }
  if (typeof asset === 'object' && asset !== null && 'amount' in asset) {
    const a = (asset as { amount: string | number }).amount
    if (typeof a === 'string') return parseInt(a, 10)
    if (typeof a === 'number') return Math.trunc(a)
  }
  return 0
}

/**
 * Port of `calc_powerup_fee` from eosio.system `powerup.cpp`.
 * Fee is in core symbol smallest units (e.g. 1/10000 EOS).
 */
function calcPowerupFee(state: PowerupStateResource, utilizationIncrease: number): number {
  if (utilizationIncrease <= 0) return 0

  const minPrice = parseAssetAmount(state.min_price)
  const maxPrice = parseAssetAmount(state.max_price)
  const weight = parseInt64(state.weight)
  const exponent = typeof state.exponent === 'number' && Number.isFinite(state.exponent) ? state.exponent : 2
  const utilization = parseInt64(state.utilization)
  const adjustedUtilization = parseInt64(state.adjusted_utilization)

  if (!Number.isFinite(weight) || weight <= 0) return NaN
  if (!Number.isFinite(utilization) || !Number.isFinite(adjustedUtilization)) return NaN
  if (!Number.isFinite(minPrice) || !Number.isFinite(maxPrice)) return NaN

  const priceIntegralDelta = (startUtilization: number, endUtilization: number): number => {
    const coefficient = (maxPrice - minPrice) / exponent
    const startU = startUtilization / weight
    const endU = endUtilization / weight
    return (
      minPrice * endU -
      minPrice * startU +
      coefficient * Math.pow(endU, exponent) -
      coefficient * Math.pow(startU, exponent)
    )
  }

  const priceFunction = (u: number): number => {
    let price = minPrice
    const newExponent = exponent - 1.0
    if (newExponent <= 0) {
      return maxPrice
    }
    price += (maxPrice - minPrice) * Math.pow(u / weight, newExponent)
    return price
  }

  let fee = 0
  let startUtilization = utilization
  const endUtilization = startUtilization + utilizationIncrease

  if (startUtilization < adjustedUtilization) {
    const span = Math.min(utilizationIncrease, adjustedUtilization - startUtilization)
    fee += priceFunction(adjustedUtilization) * (span / weight)
    startUtilization = adjustedUtilization
  }

  if (startUtilization < endUtilization) {
    fee += priceIntegralDelta(startUtilization, endUtilization)
  }

  return Math.ceil(fee)
}

/** Fraction base used by the `powerup` action (cpu_frac/net_frac are out of this). */
const POWERUP_FRAC_BASE = 1_000_000_000_000_000

async function fetchPowupState(): Promise<PowupStateRow | null> {
  const body = {
    code: 'eosio',
    scope: '', // powup.state is a singleton table — scope is empty, not "eosio"
    table: 'powup.state',
    json: true,
    limit: 1,
  }

  for (const base of EOS_RPC_ENDPOINTS) {
    const url = `${base.replace(/\/$/, '')}/v1/chain/get_table_rows`
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) continue
      const data = (await res.json()) as { rows?: PowupStateRow[] }
      const row = data.rows?.[0]
      if (row && row.cpu && row.net) return row
    } catch (e) {
      console.error('[chain-eos] estimatePowerUpCost: RPC request failed', url, e)
    }
  }
  return null
}

/**
 * Rough utilization delta from UI inputs (aligned with PowerUp weight_ratio scale).
 * Maps ms / bytes to on-chain utilization units via weight_ratio / 1_000_000.
 */
function requestedUtilizationDelta(
  amount: number,
  weightRatio: number,
): number {
  if (amount <= 0 || !Number.isFinite(amount)) return 0
  if (!Number.isFinite(weightRatio) || weightRatio <= 0) return 0
  return Math.max(0, Math.round((amount * weightRatio) / 1_000_000))
}

/**
 * Estimates the combined CPU + NET PowerUp fee for the exact `cpu_frac` / `net_frac`
 * values that will be sent in the `powerup` action (out of POWERUP_FRAC_BASE = 1e15).
 * This mirrors on-chain `powerup.cpp`: utilization_increase = frac * weight / frac_base.
 * Returns the fee in core-symbol smallest units (e.g. 1/10000 EOS), or null if unavailable.
 */
export async function estimatePowerUpFeeFromFracs(
  cpuFrac: number,
  netFrac: number,
): Promise<number | null> {
  try {
    const row = await fetchPowupState()
    if (!row?.cpu || !row.net) {
      console.error('[chain-eos] estimatePowerUpFeeFromFracs: powup.state missing or empty rows')
      return null
    }

    const cpuWeight = parseInt64(row.cpu.weight)
    const netWeight = parseInt64(row.net.weight)
    if (!Number.isFinite(cpuWeight) || !Number.isFinite(netWeight)) return null

    const cpuInc = Math.floor((cpuFrac * cpuWeight) / POWERUP_FRAC_BASE)
    const netInc = Math.floor((netFrac * netWeight) / POWERUP_FRAC_BASE)

    const cpuFee = calcPowerupFee(row.cpu, cpuInc)
    const netFee = calcPowerupFee(row.net, netInc)

    if (!Number.isFinite(cpuFee) || !Number.isFinite(netFee)) return null

    return cpuFee + netFee
  } catch (e) {
    console.error('[chain-eos] estimatePowerUpFeeFromFracs: unexpected error', e)
    return null
  }
}

/**
 * Estimates combined CPU + NET PowerUp fee using live `powup.state` and the
 * on-chain `calc_powerup_fee` integral (eosio.system `powerup.cpp`).
 */
export async function estimatePowerUpCost(cpuMs: number, netBytes: number): Promise<string> {
  try {
    const row = await fetchPowupState()
    if (!row?.cpu || !row.net) {
      console.error('[chain-eos] estimatePowerUpCost: powup.state missing or empty rows')
      return 'unavailable'
    }

    const cpuRatio = parseInt64(row.cpu.weight_ratio)
    const netRatio = parseInt64(row.net.weight_ratio)
    const cpuInc = requestedUtilizationDelta(cpuMs, cpuRatio)
    const netInc = requestedUtilizationDelta(netBytes, netRatio)

    const cpuFee = calcPowerupFee(row.cpu, cpuInc)
    const netFee = calcPowerupFee(row.net, netInc)

    if (!Number.isFinite(cpuFee) || !Number.isFinite(netFee)) {
      console.error('[chain-eos] estimatePowerUpCost: invalid fee calculation', {
        cpuFee,
        netFee,
      })
      return 'unavailable'
    }

    const totalAtomic = cpuFee + netFee
    const eos = totalAtomic / 10_000
    return `${eos.toFixed(4)} EOS`
  } catch (e) {
    console.error('[chain-eos] estimatePowerUpCost: unexpected error', e)
    return 'unavailable'
  }
}

// ─── RAM pricing ──────────────────────────────────────────────────────────────

/**
 * Live RAM price, read from the `eosio:rammarket` Bancor relay.
 *
 * The old UI multiplied by a hardcoded 0.00005 EOS/byte, which was roughly seven
 * times too cheap — you were quoted 0.05 EOS for a kilobyte that actually cost
 * 0.34. Nothing was overcharged (buyrambytes buys the exact byte count you ask
 * for), but the number on screen was fiction, so it looked like the purchase had
 * bought far less than it paid for.
 */
export interface RamPrice {
  /** EOS per byte, fees included. */
  eosPerByte: number
  /** EOS per kilobyte, fees included — the number worth showing. */
  eosPerKb: number
}

/** eosio charges 0.5% on the way in and 0.5% on the way out. */
const RAM_FEE = 1.005 / 0.995

let ramPriceCache: { value: RamPrice; at: number } | null = null
const RAM_PRICE_TTL_MS = 60_000

export async function fetchRamPrice(options: { force?: boolean } = {}): Promise<RamPrice | null> {
  if (!options.force && ramPriceCache && Date.now() - ramPriceCache.at < RAM_PRICE_TTL_MS) {
    return ramPriceCache.value
  }

  for (const url of EOS_RPC_ENDPOINTS) {
    try {
      const res = await fetch(`${url}/v1/chain/get_table_rows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          json: true,
          code: SYSTEM_CONTRACT,
          scope: SYSTEM_CONTRACT,
          table: 'rammarket',
          limit: 1,
        }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as {
        rows?: { base?: { balance?: string }; quote?: { balance?: string } }[]
      }
      const row = body.rows?.[0]
      const baseBytes = parseFloat(String(row?.base?.balance ?? '').split(' ')[0])
      const quoteEos = parseFloat(String(row?.quote?.balance ?? '').split(' ')[0])
      if (!isFinite(baseBytes) || !isFinite(quoteEos) || baseBytes <= 0) continue

      // Marginal price of one byte. Good to well under a percent for the sizes
      // a wallet buys, and it does not need to be better than that.
      const eosPerByte = (quoteEos / baseBytes) * RAM_FEE
      const value: RamPrice = { eosPerByte, eosPerKb: eosPerByte * 1024 }
      ramPriceCache = { value, at: Date.now() }
      return value
    } catch {
      // next endpoint
    }
  }
  return ramPriceCache?.value ?? null
}

/** Bancor is not linear, so spending N EOS is solved exactly rather than divided. */
export function ramBytesForEos(eos: number, price: RamPrice): number {
  if (!(eos > 0)) return 0
  return Math.floor(eos / price.eosPerByte)
}

export function ramEosForBytes(bytes: number, price: RamPrice): number {
  if (!(bytes > 0)) return 0
  return bytes * price.eosPerByte
}
